import { SavedView } from "../../../../types";
import { useEffect, useMemo, useState } from "react";
import { removeNullsFromObject } from "../../../../helpers/utility/removeNullsFromObject";
import { isObjectLike } from "lodash";
import axios from "axios";
import { useGetViewTrendApiV1DatasetDatasetIdViewsViewIdTrendGet } from "../../../../vl-api/vlComponents";
import dayjs from "dayjs";

export type PreviewVqlOverride = {
  id: string;
  created_at: { op: "lte"; value: string };
};

const getViewUrlParams = (
  view: SavedView,
  filtersOverride?: SavedView["filters"],
) => {
  const filters = filtersOverride ?? view.filters;
  const cleaned = removeNullsFromObject(filters);
  return Object.keys(cleaned)
    .map((key) => {
      const value = cleaned[key];
      return `${key}=${isObjectLike(value) ? JSON.stringify(value) : value}`;
    })
    .join("&");
};

const mergePreviewVql = (
  view: SavedView,
  barClickVql: PreviewVqlOverride,
): NonNullable<SavedView["filters"]["vql"]> => {
  const base = view.filters.vql ? [...view.filters.vql] : [];
  const sameOpIndex = base.findIndex(
    (item) =>
      "created_at" in item && item.created_at?.op === barClickVql.created_at.op,
  );
  if (sameOpIndex >= 0) {
    const next = [...base];
    next[sameOpIndex] = barClickVql;
    return next;
  }
  return [...base, barClickVql];
};

export { mergePreviewVql };

export const useSavedViewPreviewAndMonitoring = (
  datasetId: string,
  view: SavedView,
) => {
  const { data, isLoading } =
    useGetViewTrendApiV1DatasetDatasetIdViewsViewIdTrendGet({
      pathParams: { datasetId, viewId: view.id },
    });
  const [imageUrls, setImagesUrls] = useState<string[] | null>(null);
  const [previewVql, setPreviewVql] = useState<PreviewVqlOverride | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (previewVql != null) {
        setIsPreviewLoading(true);
      }
      try {
        const filters =
          previewVql != null
            ? { ...view.filters, vql: mergePreviewVql(view, previewVql) }
            : undefined;
        const params = getViewUrlParams(view, filters);
        const { data } = await axios(`/api/v1/explore/${datasetId}?${params}`);
        setImagesUrls(
          data.clusters
            .map(({ previews }: any) =>
              previews.map((preview: any) => preview.media_thumb_uri),
            )
            .flat()
            .slice(0, 30),
        );
      } finally {
        setIsPreviewLoading(false);
      }
    };
    loadData();
  }, [datasetId, view, previewVql]);
  const format = (time: string) =>
    dayjs(time + "Z")
      .local()
      .format("YYYY-MM-DD HH:mm:ss");
  const trendPoints = useMemo(() => {
    const initialPoint = {
      timestamp: format(view.createdAt),
      timestampRaw: view.createdAt,
      batch_id: "",
      count: view.stats.n_objects || view.stats.n_images,
      cumulative: 0,
    };
    const points =
      data?.points
        ?.filter((p) => p.count > 0)
        .map((p) => ({
          ...p,
          timestamp: format(p.timestamp),
          timestampRaw: p.timestamp,
        })) ?? [];
    return [initialPoint].concat(points as any);
  }, [data, view]);
  return {
    isLoading: isLoading || !imageUrls,
    imageUrls,
    trendPoints: trendPoints ?? [],
    setPreviewVql,
    previewVql,
    isPreviewLoading,
  };
};
